"use strict";
/**
 * Baseball Mode UI
 * Handles visibility toggling and factor control for Baseball Mode order buttons.
 * Also manages offense/defense column display and event-type indicator.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BASEBALL_DEFENSE_BASE_SLIPPAGE = exports.BASEBALL_MISC_BASE_FACTOR = exports.BASEBALL_BASE_SLIPPAGE = void 0;
exports.getBaseballSlippage = getBaseballSlippage;
exports.getBaseballMiscSlippage = getBaseballMiscSlippage;
exports.getBaseballDefenseSlippage = getBaseballDefenseSlippage;
exports.updateBaseballSlippageHints = updateBaseballSlippageHints;
exports.updateBaseballFactorDisplay = updateBaseballFactorDisplay;
exports.updateEventTypeIndicator = updateEventTypeIndicator;
exports.updateBaseballEventIndicator = updateBaseballEventIndicator;
exports.updateBaseballModeVisibility = updateBaseballModeVisibility;
exports.updateBaseballOffenseDefense = updateBaseballOffenseDefense;
exports.updateHalfInningIndicator = updateHalfInningIndicator;
exports.updateResetOffenseVisibility = updateResetOffenseVisibility;
exports.updateBaseballManualPriceDisplay = updateBaseballManualPriceDisplay;
exports.updateBaseballButtonHintText = updateBaseballButtonHintText;
const event_type_service_1 = require("../services/event-type-service");
const state_1 = require("../state");
const side_label_1 = require("./side-label");
const sport_mode_1 = require("./sport-mode");
// ---------------------------------------------------------------------------
// Offensive slippage (hits)
// ---------------------------------------------------------------------------
/**
 * Base slippage percentages for each hit type.
 * The factor curve starts lower and ramps up progressively.
 */
exports.BASEBALL_BASE_SLIPPAGE = {
    single: 0,
    double: 1,
    triple: 3,
    hr: 6,
};
exports.BASEBALL_MISC_BASE_FACTOR = 2;
const BASEBALL_FACTOR_CURVE = {
    single: { linear: 0.5, quadratic: 0.03 },
    double: { linear: 0.7, quadratic: 0.04 },
    triple: { linear: 1.0, quadratic: 0.06 },
    hr: { linear: 1.25, quadratic: 0.08 },
};
/**
 * Calculate effective slippage for a given hit type and factor.
 * Higher hit types are more aggressive; see `evaluateSlippageCurve` for the
 * formula.
 */
function getBaseballSlippage(hit, factor) {
    const curve = {
        base: exports.BASEBALL_BASE_SLIPPAGE[hit] ?? 0,
        ...(BASEBALL_FACTOR_CURVE[hit] ?? BASEBALL_FACTOR_CURVE.single),
    };
    return (0, sport_mode_1.evaluateSlippageCurve)(curve, factor);
}
/**
 * Calculate slippage for the bottom "MISC" baseball buy button.
 * This starts at factor 2 and scales directly with the current factor.
 */
function getBaseballMiscSlippage(factor) {
    return Math.max(0, exports.BASEBALL_MISC_BASE_FACTOR + factor);
}
// ---------------------------------------------------------------------------
// Defensive slippage (outs)
// ---------------------------------------------------------------------------
/**
 * Base slippage percentages for each defensive out type.
 * Outs are generally favorable for the defensive side so slippage starts low.
 */
exports.BASEBALL_DEFENSE_BASE_SLIPPAGE = {
    strikeout: 0,
    out: 1,
    double_play: 2,
    triple_play: 3,
};
const BASEBALL_DEFENSE_DEFAULT_CURVE = { linear: 0.5, quadratic: 0.03 };
const BASEBALL_DEFENSE_FACTOR_CURVE = {
    strikeout: { linear: 0.4, quadratic: 0.02 },
    out: { linear: 0.5, quadratic: 0.03 },
    double_play: { linear: 0.9, quadratic: 0.05 },
    triple_play: { linear: 1.2, quadratic: 0.07 },
};
/**
 * Calculate effective slippage for a defensive out type and factor.
 */
function getBaseballDefenseSlippage(outType, factor) {
    const curve = {
        base: exports.BASEBALL_DEFENSE_BASE_SLIPPAGE[outType] ?? 0,
        ...(BASEBALL_DEFENSE_FACTOR_CURVE[outType] ?? BASEBALL_DEFENSE_DEFAULT_CURVE),
    };
    return (0, sport_mode_1.evaluateSlippageCurve)(curve, factor);
}
// ---------------------------------------------------------------------------
// Slippage hint updates
// ---------------------------------------------------------------------------
/**
 * Update all slippage hint labels on offensive baseball buttons for the given factor.
 *
 * In combined mode (baseball + manual price), the main-button and MISC hint
 * elements show manual prices managed by refreshAllHints(); this function
 * only updates them when NOT in combined mode, avoiding a flash of stale
 * slippage values. EXT buttons have no slippage hint.
 */
function updateBaseballSlippageHints(factor) {
    const { tradingSettings } = (0, state_1.getState)();
    const combined = (0, sport_mode_1.isCombinedPricingMode)(tradingSettings);
    if (combined)
        return;
    const hits = ["single", "dbl", "trpl", "hr"];
    const hitKeys = ["single", "double", "triple", "hr"];
    const sides = ["a", "b"];
    for (const side of sides) {
        const miscTop = document.getElementById(`bb-misc-top-${side}-slip`);
        if (miscTop) {
            miscTop.textContent = "0%";
        }
        const miscBottom = document.getElementById(`bb-misc-bottom-${side}-slip`);
        if (miscBottom) {
            miscBottom.textContent = `${getBaseballMiscSlippage(factor)}%`;
        }
    }
    for (let i = 0; i < hits.length; i++) {
        for (const side of sides) {
            const el = document.getElementById(`bb-${hits[i]}-${side}-hint`);
            if (el) {
                const slippage = getBaseballSlippage(hitKeys[i], factor);
                el.textContent = `${slippage}%`;
            }
        }
    }
    // Defensive button hints
    const defTypes = ["ko", "out", "dp", "tp"];
    const defKeys = ["strikeout", "out", "double_play", "triple_play"];
    for (const side of sides) {
        const miscTop = document.getElementById(`bb-misc-def-top-${side}-slip`);
        if (miscTop) {
            miscTop.textContent = "0%";
        }
        const miscBottom = document.getElementById(`bb-misc-def-bottom-${side}-slip`);
        if (miscBottom) {
            miscBottom.textContent = `${getBaseballMiscSlippage(factor)}%`;
        }
    }
    for (let i = 0; i < defTypes.length; i++) {
        for (const side of sides) {
            const el = document.getElementById(`bb-${defTypes[i]}-${side}-hint`);
            if (el) {
                const slippage = getBaseballDefenseSlippage(defKeys[i], factor);
                el.textContent = `${slippage}%`;
            }
        }
    }
}
/**
 * Update the factor display and slider.
 */
function updateBaseballFactorDisplay(factor) {
    const display = document.getElementById("baseball-factor-display");
    const slider = document.getElementById("baseball-factor-slider");
    if (display) {
        display.textContent = factor.toString();
    }
    if (slider) {
        slider.value = factor.toString();
    }
    updateBaseballSlippageHints(factor);
}
// ---------------------------------------------------------------------------
// Event type indicator (generalized for all sports)
// ---------------------------------------------------------------------------
/**
 * Update the event type indicator to show the emoji for the current event type.
 * Pass null to hide the indicator entirely.
 */
function updateEventTypeIndicator(eventType) {
    const indicator = document.getElementById("event-type-indicator");
    if (!indicator)
        return;
    if (eventType) {
        indicator.textContent = event_type_service_1.EVENT_TYPE_EMOJI[eventType] ?? event_type_service_1.EVENT_TYPE_EMOJI.other;
        indicator.classList.remove("hidden");
    }
    else {
        indicator.classList.add("hidden");
    }
}
/**
 * @deprecated Use updateEventTypeIndicator instead.
 * Kept for backward-compatibility during transition.
 */
function updateBaseballEventIndicator(isBaseballEvent) {
    const indicator = document.getElementById("event-type-indicator");
    if (!indicator)
        return;
    if (isBaseballEvent) {
        indicator.textContent = event_type_service_1.EVENT_TYPE_EMOJI.baseball;
        indicator.classList.remove("hidden");
    }
    else {
        indicator.classList.add("hidden");
    }
}
// ---------------------------------------------------------------------------
// Baseball mode visibility
// ---------------------------------------------------------------------------
/**
 * Show or hide baseball mode vs traditional order buttons.
 *
 * @deprecated Prefer `updateOrderLayoutVisibility` (sport-mode.ts), which
 * also accounts for basketball mode and the force-default override.
 */
function updateBaseballModeVisibility(isBaseballMode) {
    (0, sport_mode_1.applyOrderLayout)(isBaseballMode ? "baseball" : "traditional");
}
// ---------------------------------------------------------------------------
// Offense / Defense column display
// ---------------------------------------------------------------------------
/**
 * Truncate all words in a label to their first 3 characters (uppercase).
 * e.g., "New York Mets" → "NEW YOR MET"
 */
function abbreviateAllWords(label) {
    return label.trim().split(/\s+/).map(w => w.substring(0, 3).toUpperCase()).join(" ");
}
/**
 * Update baseball columns to show offense (hit) buttons on the offensive team
 * and defense (out) buttons on the defensive team.
 *
 * When offenseTeam is null, the offense-selection UI is shown instead.
 */
function updateBaseballOffenseDefense(offenseTeam) {
    const offenseRowA = document.getElementById("bb-offense-row-a");
    const defenseRowA = document.getElementById("bb-defense-row-a");
    const offenseRowB = document.getElementById("bb-offense-row-b");
    const defenseRowB = document.getElementById("bb-defense-row-b");
    const offensePickerA = document.getElementById("bb-offense-picker-a");
    const offensePickerB = document.getElementById("bb-offense-picker-b");
    const labelA = document.getElementById("baseball-label-a");
    const labelB = document.getElementById("baseball-label-b");
    if (!offenseRowA || !defenseRowA || !offenseRowB || !defenseRowB)
        return;
    const getBaseLabel = (labelEl, fallback) => labelEl?.dataset.baseLabel || fallback;
    const baseLabelA = getBaseLabel(labelA, "Option A");
    const baseLabelB = getBaseLabel(labelB, "Option B");
    if (offenseTeam === null) {
        // Show offense picker; hide both offensive and defensive rows
        offenseRowA.classList.add("hidden");
        defenseRowA.classList.add("hidden");
        offenseRowB.classList.add("hidden");
        defenseRowB.classList.add("hidden");
        offensePickerA?.classList.remove("hidden");
        offensePickerB?.classList.remove("hidden");
        (0, side_label_1.setSideLabel)(labelA, abbreviateAllWords(baseLabelA), baseLabelA);
        (0, side_label_1.setSideLabel)(labelB, abbreviateAllWords(baseLabelB), baseLabelB);
        return;
    }
    offensePickerA?.classList.add("hidden");
    offensePickerB?.classList.add("hidden");
    if (offenseTeam === "a") {
        offenseRowA.classList.remove("hidden");
        defenseRowA.classList.add("hidden");
        offenseRowB.classList.add("hidden");
        defenseRowB.classList.remove("hidden");
        (0, side_label_1.setSideLabel)(labelA, `${abbreviateAllWords(baseLabelA)} (O)`, `${baseLabelA} (Offense)`);
        (0, side_label_1.setSideLabel)(labelB, `${abbreviateAllWords(baseLabelB)} (D)`, `${baseLabelB} (Defense)`);
    }
    else {
        offenseRowA.classList.add("hidden");
        defenseRowA.classList.remove("hidden");
        offenseRowB.classList.remove("hidden");
        defenseRowB.classList.add("hidden");
        (0, side_label_1.setSideLabel)(labelA, `${abbreviateAllWords(baseLabelA)} (D)`, `${baseLabelA} (Defense)`);
        (0, side_label_1.setSideLabel)(labelB, `${abbreviateAllWords(baseLabelB)} (O)`, `${baseLabelB} (Offense)`);
    }
}
// ---------------------------------------------------------------------------
// Half-inning indicator
// ---------------------------------------------------------------------------
/**
 * Update the half-inning indicator (▲ top / ▼ bottom).
 */
function updateHalfInningIndicator(halfInning) {
    const indicator = document.getElementById("inning-half-indicator");
    if (indicator) {
        indicator.textContent = halfInning === "top" ? "▲" : "▼";
        indicator.title = halfInning === "top" ? "Top of inning" : "Bottom of inning";
    }
}
/**
 * Update the reset offense button visibility.
 */
function updateResetOffenseVisibility(visible) {
    const btn = document.getElementById("reset-offense-btn");
    if (btn) {
        btn.classList.toggle("hidden", !visible);
    }
}
// ---------------------------------------------------------------------------
// Per-button manual price display helpers
// ---------------------------------------------------------------------------
/**
 * Show or hide the per-button price controls (steppers) and the Set-to-Ask
 * buttons that appear when a sport mode + manual price mode are both active.
 * Basketball and soccer steppers reuse the `.bb-price-stepper` class, so
 * every sport's controls are covered.
 */
function updateBaseballManualPriceDisplay(isActive) {
    document.querySelectorAll(".bb-price-stepper").forEach((el) => {
        el.classList.toggle("hidden", !isActive);
    });
    for (const id of [
        "baseball-set-to-ask-btn",
        "basketball-set-to-ask-btn",
        "soccer-set-to-ask-btn",
    ]) {
        const setToAskBtn = document.getElementById(id);
        if (setToAskBtn)
            setToAskBtn.classList.toggle("hidden", !isActive);
    }
}
/**
 * Update the hint/label text for a single baseball button.
 * Used to switch between slippage display and manual price display.
 *
 * @param hintId  - The ID of the hint `<span>` element (e.g. "bb-single-a-hint")
 * @param text    - New display text (e.g. "55¢" or "0%")
 */
function updateBaseballButtonHintText(hintId, text) {
    const el = document.getElementById(hintId);
    if (el)
        el.textContent = text;
}
